/**
 * admin/AdminWorkshops.js
 *
 * Workshops and evening events — the single dates people sign up for,
 * separate from a retreat. Anything published here appears on the public
 * workshops page and in the strip on the home page straight away.
 *
 *   list        every workshop, newest date first, with its numbers
 *   editor      one form, used for both new and existing
 *   roster      who signed up, who's waiting, promote / cancel / export
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  HiOutlineSparkles, HiOutlinePlus, HiOutlineTrash, HiOutlineXMark,
  HiOutlinePencilSquare, HiOutlineUsers, HiOutlineArrowDownTray,
  HiOutlineCheckCircle, HiOutlineClock, HiOutlineMapPin,
} from "react-icons/hi2";
import toast from "react-hot-toast";

import AdminLayout from "./AdminLayout";
import { workshopsAPI } from "../services/api";
import ImagePicker from "./ImagePicker";
import { useCurrentAdmin, hasPermission } from "./permissions";
import "./AdminWorkshops.css";

const KINDS = [
  { v: "workshop", label: "Workshop" },
  { v: "event", label: "Event" },
  { v: "ceremony", label: "Ceremony" },
];

const STATUSES = [
  { v: "draft", label: "Draft", hint: "Only you can see it" },
  { v: "published", label: "Published", hint: "Live on the website" },
  { v: "cancelled", label: "Cancelled", hint: "Hidden, sign-ups kept" },
];

const WHEN_TABS = [
  { v: "upcoming", label: "Upcoming" },
  { v: "past", label: "Past" },
  { v: "", label: "All" },
];

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/* Dates come back as wall-clock strings ("2026-10-18 18:00:00"), so they
   are read apart rather than handed to Date, which would treat them as UTC
   and shift the evening onto the wrong day. */
const parts = (s) => {
  const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return m ? { y: +m[1], mo: +m[2], d: +m[3], hh: +m[4], mi: +m[5] } : null;
};
const asDate = (s) => { const p = parts(s); return p ? new Date(p.y, p.mo - 1, p.d, p.hh, p.mi) : null; };

const fmtWhen = (s) => {
  const d = asDate(s);
  if (!d) return "—";
  return d.toLocaleString("en-US", { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
};

/** "2026-10-18 18:00:00" → "2026-10-18T18:00" for <input type="datetime-local">. */
const toInput = (s) => { const p = parts(s); return p ? `${s.slice(0, 10)}T${s.slice(11, 16)}` : ""; };

const isPast = (s) => { const d = asDate(s); return d ? d.getTime() < Date.now() : false; };

/* ══════════════════════════════════════════════════════════
   EDITOR
══════════════════════════════════════════════════════════ */
const BLANK = {
  title: "", kind: "workshop", summary: "", description: "", image_path: "",
  location: "", starts_at: "", ends_at: "", capacity: 20, price: 0,
  signup_required: true, status: "draft", slug: "",
};

function WorkshopEditor({ workshop, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    workshop
      ? { ...workshop, starts_at: toInput(workshop.starts_at), ends_at: toInput(workshop.ends_at),
          summary: workshop.summary || "", description: workshop.description || "",
          image_path: workshop.image_path || "", location: workshop.location || "" }
      : BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
    setError("");
  };

  const save = async () => {
    if (!form.title.trim()) { setError("Give it a title."); return; }
    if (!form.starts_at) { setError("Set the date and time it starts."); return; }
    if (form.ends_at && form.ends_at < form.starts_at) { setError("It can't finish before it starts."); return; }

    setSaving(true); setError("");
    try {
      const payload = { ...form, capacity: Number(form.capacity) || 0, price: Number(form.price) || 0 };
      if (workshop) await workshopsAPI.update(workshop.id, payload);
      else await workshopsAPI.create(payload);
      toast.success(workshop ? "Saved." : "Workshop created.");
      onSaved();
    } catch (err) {
      setError(err.message || "Couldn't save that.");
    } finally { setSaving(false); }
  };

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal awk__modal" onClick={(e) => e.stopPropagation()}>
        <div className="aap__contact-head">
          <h3>{workshop ? "Edit Workshop" : "New Workshop"}</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark /></button>
        </div>

        <div className="ars__field">
          <label>Title</label>
          <input className="arb__form-input" value={form.title} placeholder="e.g. Cacao Circle" onChange={set("title")} />
        </div>

        <div className="awk__row">
          <div className="ars__field">
            <label>Kind</label>
            <div className="ars__type-row">
              {KINDS.map((k) => (
                <button key={k.v} type="button"
                  className={`ars__type-btn ${form.kind === k.v ? "ars__type-btn--active" : ""}`}
                  onClick={() => setForm((f) => ({ ...f, kind: k.v }))}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="awk__row">
          <div className="ars__field">
            <label>Starts</label>
            <input className="arb__form-input" type="datetime-local" value={form.starts_at} onChange={set("starts_at")} />
          </div>
          <div className="ars__field">
            <label>Ends <span className="ars__optional">(optional)</span></label>
            <input className="arb__form-input" type="datetime-local" value={form.ends_at} onChange={set("ends_at")} />
          </div>
        </div>

        <p className="awk__hint">
          Times are the time on site — they show exactly as typed, wherever someone is reading from.
        </p>

        <div className="ars__field">
          <label>Where <span className="ars__optional">(optional)</span></label>
          <input className="arb__form-input" value={form.location} placeholder="e.g. By the waterfall" onChange={set("location")} />
        </div>

        <div className="awk__row">
          <div className="ars__field">
            <label>Places</label>
            <input className="arb__form-input" type="number" min={0} value={form.capacity} onChange={set("capacity")} />
            <span className="ars__optional">0 means no limit</span>
          </div>
          <div className="ars__field">
            <label>Price per person</label>
            <input className="arb__form-input" type="number" min={0} step="0.01" value={form.price} onChange={set("price")} />
            <span className="ars__optional">0 shows as Free</span>
          </div>
        </div>

        <div className="ars__field">
          <label>Short line for the card</label>
          <input className="arb__form-input" value={form.summary} maxLength={400}
            placeholder="One sentence — this is what people read first." onChange={set("summary")} />
        </div>

        <div className="ars__field">
          <label>Full description <span className="ars__optional">(optional)</span></label>
          <textarea className="arb__form-input" rows={3} value={form.description}
            placeholder="Shown when someone opens the sign-up form." onChange={set("description")} />
        </div>

        <ImagePicker
          label="Picture"
          value={form.image_path}
          onChange={(v) => { setForm((f) => ({ ...f, image_path: v })); setError(""); }}
        />

        <label className="awk__check">
          <input type="checkbox" checked={!!form.signup_required} onChange={set("signup_required")} />
          <span>People need to sign up
            <em> — turn this off for a drop-in, and the card will say so instead of showing a button.</em>
          </span>
        </label>

        <div className="ars__field">
          <label>Status</label>
          <div className="ars__type-row">
            {STATUSES.map((s) => (
              <button key={s.v} type="button" title={s.hint}
                className={`ars__type-btn ${form.status === s.v ? "ars__type-btn--active" : ""}`}
                onClick={() => setForm((f) => ({ ...f, status: s.v }))}>
                {s.label}
              </button>
            ))}
          </div>
          <span className="ars__optional">{STATUSES.find((s) => s.v === form.status)?.hint}</span>
        </div>

        {workshop && (
          <div className="ars__field">
            <label>Web address <span className="ars__optional">(optional)</span></label>
            <input className="arb__form-input" value={form.slug || ""} onChange={set("slug")} />
            <span className="ars__optional">Leave it alone unless you need to change the link.</span>
          </div>
        )}

        {error && <p className="awk__error">{error}</p>}

        <div className="awk__actions">
          <button type="button" className="a-btn a-btn--rust" onClick={save} disabled={saving}>
            {saving ? "Saving…" : workshop ? "Save changes" : "Create workshop"}
          </button>
          <button type="button" className="a-btn a-btn--outline" onClick={onClose} disabled={saving}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   ROSTER
══════════════════════════════════════════════════════════ */
function Roster({ workshop, canEdit, onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await workshopsAPI.signups(workshop.id);
      setRows(res.data || []);
    } catch (err) { toast.error(err.message || "Couldn't load the list."); }
    finally { setLoading(false); }
  }, [workshop.id]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (row, status) => {
    setBusy(true);
    try {
      const res = await workshopsAPI.updateSignup(workshop.id, row.id, status);
      if (res?.data?.over > 0) toast(res.message, { icon: "⚠️", duration: 6000 });
      else toast.success(status === "confirmed" ? `${row.name} is confirmed — we've emailed them.` : "Updated.");
      await load();
      onChanged();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    setBusy(true);
    try {
      await workshopsAPI.removeSignup(workshop.id, row.id);
      toast.success("Removed.");
      await load();
      onChanged();
    } catch (err) { toast.error(err.message || "Failed."); }
    finally { setBusy(false); }
  };

  const confirmed = rows.filter((r) => r.status === "confirmed");
  const waiting = rows.filter((r) => r.status === "waitlist");
  const coming = confirmed.reduce((s, r) => s + r.places, 0);
  const over = workshop.capacity > 0 ? Math.max(0, coming - workshop.capacity) : 0;

  return (
    <div className="aap__contact-overlay" onClick={onClose}>
      <div className="aap__contact-modal awk__modal" onClick={(e) => e.stopPropagation()}>
        <div className="aap__contact-head">
          <h3>Who's coming — {workshop.title}</h3>
          <button type="button" onClick={onClose}><HiOutlineXMark /></button>
        </div>

        <p className="awk__hint awk__hint--top">
          {fmtWhen(workshop.starts_at)}
          {workshop.location ? ` · ${workshop.location}` : ""}
          {" · "}
          {coming} coming
          {workshop.capacity > 0 && (
            over > 0
              ? <span className="awk__over"> — {over} over the {workshop.capacity} places</span>
              : ` of ${workshop.capacity}`
          )}
          {waiting.length ? ` · ${waiting.length} waiting` : ""}
        </p>

        {loading ? (
          <div className="arb__loading"><div className="acr__spinner" /></div>
        ) : rows.length === 0 ? (
          <p className="awk__empty">Nobody has signed up yet.</p>
        ) : (
          <div className="awk__roster">
            {rows.map((r) => (
              <div key={r.id} className={`awk__signup awk__signup--${r.status}`}>
                <div className="awk__signup-who">
                  <p className="awk__signup-name">
                    {r.name}
                    {r.places > 1 && <span className="awk__pill">{r.places} places</span>}
                    <span className={`awk__status awk__status--${r.status}`}>{r.status}</span>
                  </p>
                  <p className="awk__signup-contact">
                    <a href={`mailto:${r.email}`}>{r.email}</a>
                    {r.phone ? ` · ${r.phone}` : ""}
                  </p>
                  {r.notes && <p className="awk__signup-notes">“{r.notes}”</p>}
                </div>

                {canEdit && (
                  <div className="awk__signup-actions">
                    {r.status !== "confirmed" && (
                      <button type="button" className="a-btn a-btn--outline awk__mini" disabled={busy}
                        onClick={() => setStatus(r, "confirmed")}>Confirm</button>
                    )}
                    {r.status === "confirmed" && (
                      <button type="button" className="a-btn a-btn--outline awk__mini" disabled={busy}
                        onClick={() => setStatus(r, "cancelled")}>Cancel</button>
                    )}
                    <button type="button" className="awk__icon-btn" title="Remove from the list"
                      disabled={busy} onClick={() => remove(r)}>
                      <HiOutlineTrash />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="awk__actions">
          <button type="button" className="a-btn a-btn--outline" disabled={rows.length === 0}
            onClick={() => workshopsAPI.exportCsv(workshop.id, workshop.title).catch((e) => toast.error(e.message))}>
            <HiOutlineArrowDownTray /> Export CSV
          </button>
          <button type="button" className="a-btn a-btn--outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   PAGE
══════════════════════════════════════════════════════════ */
export default function AdminWorkshops() {
  const { admin, loading: adminLoading } = useCurrentAdmin();
  const canEdit = hasPermission(admin, "workshops.edit");
  /* Workshops are Makua's. The nav hides this on Forge, but someone can
     still type the address, so say so plainly instead of letting the page
     ask a database that has no workshops table. */
  const available = !admin?.product || admin.product === "makua";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [when, setWhen] = useState("upcoming");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);   // workshop | "new" | null
  const [roster, setRoster] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    if (!available) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await workshopsAPI.list({ when });
      setItems(res.data || []);
      /* The API answers with an empty list and an explanation when the
         tables aren't there, rather than a raw SQL error. */
      setNotice(res.data?.length === 0 && res.message && res.message !== "Success" ? res.message : null);
    } catch (err) { toast.error(err.message || "Couldn't load workshops."); }
    finally { setLoading(false); }
  }, [when, available]);

  useEffect(() => { if (!adminLoading) load(); }, [load, adminLoading]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((w) =>
      w.title.toLowerCase().includes(needle) || (w.location || "").toLowerCase().includes(needle));
  }, [items, q]);

  const stats = useMemo(() => ({
    published: items.filter((w) => w.status === "published").length,
    coming: items.reduce((s, w) => s + w.confirmed, 0),
    waiting: items.reduce((s, w) => s + w.waitlisted, 0),
  }), [items]);

  const remove = (w) => {
    toast((t) => (
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        <p style={{ margin: 0, fontWeight: 700 }}>Delete “{w.title}”?</p>
        <p style={{ margin: 0, fontSize: "0.78rem", color: "#6b6258" }}>This can't be undone.</p>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button onClick={async () => {
            toast.dismiss(t.id);
            try { await workshopsAPI.remove(w.id); toast.success("Deleted."); load(); }
            catch (err) { toast.error(err.message || "Failed."); }
          }} style={{ background: "#dc2626", color: "#fff", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontWeight: 700, fontSize: "0.78rem" }}>Delete</button>
          <button onClick={() => toast.dismiss(t.id)}
            style={{ background: "#f3f4f0", border: "none", borderRadius: "5px", padding: "0.35rem 0.75rem", cursor: "pointer", fontSize: "0.78rem" }}>Cancel</button>
        </div>
      </div>
    ), { duration: Infinity, style: { maxWidth: "330px" } });
  };

  return (
    <AdminLayout
      pageTitle="Workshops & Events"
      pageSubtitle="Single dates people sign up for, separate from a retreat."
      permission="workshops.view"
      feature="workshops"
    >
      {adminLoading || loading ? (
        <div className="arb__loading"><div className="acr__spinner" /></div>
      ) : !available ? (
        <div className="acard awk__empty-card">
          <HiOutlineSparkles />
          <p>
            Workshops are part of Makua, not Forge — this admin is pointed at the Forge site.
            <br />
            You'll find them in the Makua admin.
          </p>
        </div>
      ) : (
        <>
          {notice && <div className="acard awk__notice">{notice}</div>}
          <div className="aap__stats-row" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(var(--admin-rust-rgb), 0.1)" }}>
                  <HiOutlineSparkles style={{ color: "#e05a2b", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">Published</p>
              </div>
              <p className="aap__stat-value">{stats.published}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(22,163,74,0.1)" }}>
                  <HiOutlineCheckCircle style={{ color: "#16a34a", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">People coming</p>
              </div>
              <p className="aap__stat-value">{stats.coming}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(107,98,88,0.1)" }}>
                  <HiOutlineClock style={{ color: "#6b6258", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">On waiting lists</p>
              </div>
              <p className="aap__stat-value">{stats.waiting}</p>
            </div>
          </div>

          <div className="awk__toolbar">
            <div className="ars__type-row">
              {WHEN_TABS.map((t) => (
                <button key={t.label} type="button"
                  className={`ars__type-btn ${when === t.v ? "ars__type-btn--active" : ""}`}
                  onClick={() => setWhen(t.v)}>{t.label}</button>
              ))}
            </div>

            <input className="arb__form-input awk__search" value={q} placeholder="Search by title or place…"
              onChange={(e) => setQ(e.target.value)} />

            {canEdit && (
              <button type="button" className="a-btn a-btn--rust" onClick={() => setEditing("new")}>
                <HiOutlinePlus /> New Workshop
              </button>
            )}
          </div>

          {shown.length === 0 ? (
            <div className="acard awk__empty-card">
              <HiOutlineSparkles />
              <p>{q ? "Nothing matches that search." : when === "past" ? "Nothing has happened yet." : "No workshops scheduled."}</p>
              {canEdit && !q && (
                <button type="button" className="a-btn a-btn--rust" onClick={() => setEditing("new")}>
                  <HiOutlinePlus /> Add the first one
                </button>
              )}
            </div>
          ) : (
            <div className="awk__list">
              {shown.map((w) => (
                <div key={w.id} className={`acard awk__card ${isPast(w.starts_at) ? "awk__card--past" : ""}`}>
                  <div className="awk__card-main">
                    <p className="awk__card-when">{fmtWhen(w.starts_at)}</p>
                    <h3 className="awk__card-title">
                      {w.title}
                      <span className={`awk__status awk__status--${w.status}`}>{w.status}</span>
                      {!w.signup_required && <span className="awk__pill">drop-in</span>}
                    </h3>
                    {w.summary && <p className="awk__card-summary">{w.summary}</p>}
                    <p className="awk__card-meta">
                      {w.location && <><HiOutlineMapPin /> {w.location} · </>}
                      {w.price > 0 ? money(w.price) : "Free"} ·{" "}
                      {w.capacity === 0
                        ? `${w.confirmed} signed up, no limit`
                        : w.confirmed > w.capacity
                          ? <span className="awk__over">{w.confirmed} signed up — {w.confirmed - w.capacity} over the {w.capacity} places</span>
                          : `${w.confirmed} of ${w.capacity} taken${w.placesLeft === 0 ? " — full" : ""}`}
                      {w.waitlisted > 0 && ` · ${w.waitlisted} waiting`}
                    </p>
                  </div>

                  <div className="awk__card-actions">
                    <button type="button" className="a-btn a-btn--outline awk__mini" onClick={() => setRoster(w)}>
                      <HiOutlineUsers /> {w.confirmed + w.waitlisted || "0"}
                    </button>
                    {canEdit && (
                      <>
                        <button type="button" className="awk__icon-btn" title="Edit" onClick={() => setEditing(w)}>
                          <HiOutlinePencilSquare />
                        </button>
                        <button type="button" className="awk__icon-btn awk__icon-btn--danger" title="Delete"
                          onClick={() => remove(w)}>
                          <HiOutlineTrash />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <WorkshopEditor
          workshop={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {roster && (
        <Roster
          workshop={roster}
          canEdit={canEdit}
          onClose={() => setRoster(null)}
          onChanged={load}
        />
      )}
    </AdminLayout>
  );
}
